import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { useTranslation } from "@redotech/redo-i18n";
import Printer from "@redotech/redo-icons/printer.svg";
import QrCode from "@redotech/redo-icons/qr-code-01.svg";
import { TrackableType } from "@redotech/redo-order-tracking-trackable-common/trackable";
import { Trackable } from "@redotech/redo-order-tracking-trackable-return-common/trackable";
import { useMemo } from "react";
export function LabelButtons({
  trackable,
  carrier,
  commercialInvoice,
  qrCodeOnly,
  onOpenUrl,
}: {
  trackable: Trackable & { type: TrackableType.RETURN };
  carrier: string | undefined;
  commercialInvoice: string | null | undefined;
  qrCodeOnly?: boolean;
  onOpenUrl: (url: string) => void;
}) {
  const { t } = useTranslation();

  const labelButtons = useMemo(() => {
    if (trackable.shipments?.length) {
      return trackable.shipments
        .filter((shipment) => !!shipment?.postage_label)
        .map((shipment) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const originalIndex = trackable.shipments!.indexOf(shipment);
          return (
            <RedoButton
              hierarchy="secondary"
              IconLeading={Printer}
              key={originalIndex}
              onClick={() => onOpenUrl(shipment.postage_label!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              text={
                trackable.shipments?.length && trackable.shipments?.length > 1
                  ? t("Print label {{labelNumber}}", {
                      labelNumber: originalIndex + 1,
                    })
                  : t("Print label")
              }
              uiSelector="print-label-button"
            />
          );
        });
    }
    return null;
  }, [t, trackable.shipments, onOpenUrl]);

  const qrButtons = useMemo(() => {
    if (!trackable.pickup && carrier !== "UPS" && trackable.shipments?.length) {
      return trackable.shipments
        .filter((shipment) => !!shipment?.form_label)
        .map((shipment) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const originalIndex = trackable.shipments!.indexOf(shipment);
          return (
            <RedoButton
              hierarchy="secondary"
              IconLeading={QrCode}
              key={originalIndex}
              onClick={() => onOpenUrl(shipment.form_label!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              text={
                trackable.shipments?.length && trackable.shipments?.length > 1
                  ? t("QR code {{labelNumber}}", {
                      labelNumber: originalIndex + 1,
                    })
                  : t("QR code")
              }
              uiSelector="qr-code-button"
            />
          );
        });
    }
    return null;
  }, [t, trackable.shipments, trackable.pickup, carrier, onOpenUrl]);

  // When qrCodeOnly is enabled, hide the printable label if a QR code is
  // available. The label is kept as a fallback when QR generation fails.
  const hasQrCodes = qrButtons && qrButtons.length > 0;
  const showLabels = !(qrCodeOnly && hasQrCodes);

  return (
    <Flex data-ui="label-buttons" flexDirection="row" gap="sm" grow={1} pt="xs">
      {showLabels && labelButtons}
      {qrButtons}
      {!!commercialInvoice && (
        <RedoButton
          hierarchy="secondary"
          onClick={() => onOpenUrl(commercialInvoice)}
          text={t("Print commercial invoice")}
          uiSelector="print-invoice-button"
        />
      )}
    </Flex>
  );
}
